"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, Copy, Calendar, MessageSquare, Maximize2, Minimize2, Plus, Edit, ArrowRight, UserPlus } from "lucide-react";
import { useStore } from "@/lib/store";
import { createClient } from "@/lib/supabase/client";
import { StatusIcon } from "@/components/ui/StatusIcon";
import { PriorityIcon } from "@/components/ui/PriorityIcon";
import { ReportChannelValue } from "./ReportChannel";
import { Avatar } from "@/components/ui/Avatar";
import { HumanMentionPicker } from "@/components/comments/HumanMentionPicker";
import { Markdown } from "@/components/ui/Markdown";
import { AgentRunsSection } from "@/components/agent-runs/AgentRunsSection";
import { TaskAgentExecution } from "@/components/agents/TaskAgentExecution";
import { ZoomControls, useContentZoom, zoomStyle } from "@/components/ui/ContentZoom";
import { ShareMenu } from "@/components/ui/ShareMenu";
import { STATUSES, PRIORITIES } from "@/lib/constants";
import {
  StatusPicker,
  PriorityPicker,
  AssigneePicker,
  LabelPicker,
  ProjectPicker,
} from "./Pickers";
import { formatRelative, formatDate } from "@/lib/utils";
import { replaceCurrentHumanMarkdown } from "@/lib/humanMarkdown";
import { formatTaskDate } from "@/lib/taskDates";

export function IssueDetail({ canWrite }: { canWrite: boolean }) {
  const selectedIssueId = useStore((s) => s.selectedIssueId);
  const issue = useStore((s) => s.issues.find((i) => i.id === s.selectedIssueId && i.workspaceId === s.activeWorkspaceId));
  const selectIssue = useStore((s) => s.selectIssue);
  const updateIssue = useStore((s) => s.updateIssue);
  const deleteIssue = useStore((s) => s.deleteIssue);
  const addComment = useStore((s) => s.addComment);
  const users = useStore((s) => s.users);
  const labels = useStore((s) => s.labels);
  const projects = useStore((s) => s.projects);
  const teams = useStore((s) => s.teams);
  const me = useStore((s) => s.users.find((u) => u.id === s.currentUserId));

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [editingDescription, setEditingDescription] = useState(false);
  const [comment, setComment] = useState("");
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const commentGeneration = useRef(0);
  const commentPending = useRef(false);
  const commentRef = useRef<HTMLTextAreaElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const { zoom, zoomIn, zoomOut, reset: resetZoom } = useContentZoom();
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const descRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (issue) {
      setTitle(issue.title);
      setDescription(issue.description);
      ++commentGeneration.current; setComment(""); setReplyToId(null); setCommentError(null); setCommentSaving(false); commentPending.current = false;
    }
  }, [issue?.id]);

  useEffect(() => {
    const unsubscribe = useStore.subscribe((state, previous) => {
      if (state.selectedIssueId !== previous.selectedIssueId || state.activeWorkspaceId !== previous.activeWorkspaceId || state.currentUserId !== previous.currentUserId) {
        ++commentGeneration.current; commentPending.current = false; setComment(""); setReplyToId(null); setCommentError(null); setCommentSaving(false);
      }
    });
    return () => { ++commentGeneration.current; unsubscribe(); };
  }, []);

  // A rejected persisted edit restores the canonical title while this drawer
  // stays open. Preserve a new draft if the person is actively typing again.
  useEffect(() => {
    if (issue && document.activeElement !== titleRef.current) setTitle(issue.title);
  }, [issue?.id, issue?.title]);

  useEffect(() => {
    if (issue && !editingDescription) setDescription(issue.description);
  }, [issue?.id, issue?.description, editingDescription]);

  // Auto-grow the title textarea: rows={1} + resize-none means a long title
  // wraps INSIDE the box but the box stays one row tall, visually cutting the
  // title (e.g. "…que o time envia para" hiding "Alan e Wagner"). Re-measure
  // whenever the text, the issue, the zoom, or the expanded width changes.
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    const resizeTitle = () => {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    };
    resizeTitle();
    window.addEventListener("resize", resizeTitle);
    return () => window.removeEventListener("resize", resizeTitle);
  }, [title, issue?.id, zoom, expanded]);

  // Load workspace_activity rows scoped to this issue (create / update / status_change / assign / delete).
  // Comments are already on issue.comments — those come from the store.
  useEffect(() => {
    if (!issue?.id) {
      setActivity([]);
      return;
    }
    const sb = createClient();
    let cancelled = false;
    void (async () => {
      const { data } = await sb
        .from("workspace_activity")
        .select("id, actor_id, action, diff, created_at")
        .eq("entity_type", "issue")
        .eq("entity_id", issue.id)
        .neq("action", "comment") // comments rendered separately
        .order("created_at", { ascending: true })
        .limit(200);
      if (!cancelled) setActivity((data ?? []) as unknown as ActivityEvent[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [issue?.id]);

  useEffect(() => {
    if (!selectedIssueId) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      )
        return;
      if (e.key === "Escape") selectIssue(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedIssueId, selectIssue]);

  async function submitComment() {
    if (!issue || !canWrite || !comment.trim() || commentPending.current) return;
    const targetId = issue.id;
    const generation = commentGeneration.current;
    commentPending.current = true; setCommentSaving(true); setCommentError(null);
    try {
      await addComment(targetId, comment.trim(), { replyToId });
      if (commentGeneration.current === generation) { setComment(""); setReplyToId(null); }
    } catch {
      if (commentGeneration.current === generation) setCommentError("Não foi possível confirmar o comentário. Seu texto foi preservado.");
    } finally {
      if (commentGeneration.current === generation) { commentPending.current = false; setCommentSaving(false); }
    }
  }

  const visible = Boolean(selectedIssueId && issue);
  if (!issue) {
    return <AnimatePresence />;
  }

  const compatibilityAssignee = users.find((u) => u.id === issue.assigneeId);
  const owner = users.find(
    (u) =>
      u.id ===
      (issue.ownerId ??
        (compatibilityAssignee && !compatibilityAssignee.isBot
          ? compatibilityAssignee.id
          : null))
  );
  const executorAgent = users.find(
    (u) =>
      u.id ===
      (issue.executorAgentId ??
        (compatibilityAssignee?.isBot ? compatibilityAssignee.id : null))
  );
  const requester = users.find((u) => u.id === issue.requesterId && !u.isBot);
  const issueLabels = labels.filter((l) => issue.labelIds.includes(l.id));
  const project = projects.find((p) => p.id === issue.projectId);
  const team = teams.find((t) => t.id === issue.teamId);
  const statusMeta = STATUSES.find((s) => s.id === issue.status)!;
  const priorityMeta = PRIORITIES.find((p) => p.id === issue.priority)!;
  const creationDate = formatTaskDate(issue.createdAt);
  const updateDate = formatTaskDate(issue.updatedAt);

  const saveTitle = () => {
    if (title.trim() && title !== issue.title) updateIssue(issue.id, { title: title.trim() });
  };
  const saveDesc = () => {
    if (description !== issue.description) updateIssue(issue.id, { description });
  };

  return (
    <AnimatePresence>
      {visible && (
      <>
      {/* Click-outside backdrop — closes the drawer when the visible area
          to the left of the panel is clicked. Rendered above the app but
          below the drawer (z-30 vs z-40). Hidden in expanded mode since
          the drawer is fullscreen then. */}
      {!expanded && (
        <motion.div
          key={`${issue.id}-backdrop`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={() => selectIssue(null)}
          className="fixed inset-0 z-30 bg-black/25 md:bg-transparent cursor-pointer"
        />
      )}
      <motion.div
        key={issue.id}
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
        className={
          expanded
            ? "fixed inset-0 z-40 bg-[var(--bg)] shadow-2xl flex flex-col"
            : "fixed top-0 right-0 bottom-0 w-full max-w-full md:w-[960px] lg:w-[1080px] z-40 bg-[var(--bg)] border-l border-[var(--border)] shadow-2xl flex flex-col"
        }
      >
        <div className="flex min-h-11 shrink-0 items-center gap-2 border-b border-[var(--border)] bg-[var(--bg)] px-3 md:px-5">
          <span
            className="w-5 h-5 shrink-0 rounded flex items-center justify-center text-[11px]"
            style={{ background: `${team?.color}22`, color: team?.color }}
          >
            {team?.icon}
          </span>
          <span className="min-w-0 flex-1 break-all text-[12px] font-mono text-[var(--fg-secondary)]">
            {issue.identifier}
          </span>
          <button
            onClick={() => navigator.clipboard.writeText(issue.identifier)}
            className="min-w-11 min-h-11 shrink-0 rounded hover:bg-[var(--bg-hover)] flex items-center justify-center text-[var(--fg-tertiary)]"
            title="Copiar identificador"
            aria-label="Copiar identificador"
          >
            <Copy size={15} />
          </button>
          <button
            onClick={() => selectIssue(null)}
            className="min-w-11 min-h-11 shrink-0 rounded hover:bg-[var(--bg-hover)] flex items-center justify-center text-[var(--fg-secondary)]"
            title="Fechar tarefa (Esc)"
            aria-label="Fechar tarefa"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
          <div className="mx-auto w-full max-w-[860px] min-w-0">
            <div className="px-5 py-6 md:px-10 md:py-9 space-y-6 min-w-0" style={zoomStyle(zoom)}>
              <div className="space-y-2">
                <textarea
                  ref={titleRef}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={() => canWrite && saveTitle()}
                  readOnly={!canWrite}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (canWrite) titleRef.current?.blur();
                    }
                  }}
                  rows={1}
                  className="block w-full bg-transparent outline-none text-[26px] md:text-[30px] font-semibold tracking-tight leading-[1.2] resize-none overflow-hidden [overflow-wrap:anywhere] read-only:cursor-default"
                  aria-label="Título da tarefa"
                  placeholder="Task title"
                  style={{ minHeight: 34 }}
                />
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-[var(--fg-secondary)]">
                  <span className="inline-flex items-center gap-1.5"><StatusIcon status={issue.status} size={14} />{statusMeta.name}</span>
                  <span className="inline-flex min-w-0 items-center gap-1.5"><Avatar user={owner} size={18} /><span className="[overflow-wrap:anywhere]">{owner?.name || "No owner"}</span></span>
                  {canWrite ? <label className="inline-flex min-h-11 items-center gap-2 text-xs"><input type="checkbox" checked={issue.isBlocked === true} onChange={(event) => updateIssue(issue.id, { isBlocked: event.target.checked })} />Bloqueada</label> : issue.isBlocked && <span className="text-xs text-[var(--red)]">Bloqueada</span>}
                  <span className="text-[11px] leading-5 text-[var(--fg-tertiary)]">{creationDate ? `Criado em ${creationDate}` : "Criação não disponível"}</span>
                </div>
              </div>

              <details className="rounded-lg border border-[var(--border)] bg-[var(--bg-secondary)]">
                <summary className="min-h-11 cursor-pointer px-4 py-3 text-[12px] font-medium text-[var(--fg-secondary)]">Propriedades e ações</summary>
                <div className="flex flex-wrap items-center gap-2 border-t border-[var(--border)] px-4 py-2 [&_button]:min-h-11 [&_button]:min-w-11">
                  <ZoomControls zoom={zoom} zoomIn={zoomIn} zoomOut={zoomOut} reset={resetZoom} />
                  {canWrite && <ShareMenu type="issue" id={issue.id} isPublic={issue.isPublic} />}
                  <button
                    onClick={() => setExpanded((value) => !value)}
                    className="inline-flex items-center gap-1.5 rounded px-2 text-[12px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)]"
                    title={expanded ? "Collapse" : "Expand"}
                  >
                    {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                    {expanded ? "Recolher" : "Expandir"}
                  </button>
                  {canWrite && <button
                    onClick={() => { if (confirm("Delete this task?")) deleteIssue(issue.id); }}
                    className="inline-flex items-center gap-1.5 rounded px-2 text-[12px] text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)]"
                    title="Delete"
                  >
                    <Trash2 size={14} /> Excluir
                  </button>}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 px-4 pb-3 text-[11px] leading-5 text-[var(--fg-tertiary)]">
                  <span>{updateDate ? `Atualizado em ${updateDate}` : "Atualização não disponível"}</span>
                </div>
                <div className="grid grid-cols-1 gap-x-8 gap-y-4 border-t border-[var(--border)] p-4 md:grid-cols-2">
                  <PropRow label="Status">
                    {canWrite ? (
                      <StatusPicker
                        value={issue.status}
                        onChange={(v) => updateIssue(issue.id, { status: v })}
                        trigger={
                          <PropBtn>
                            <StatusIcon status={issue.status} size={13} />
                            <span>{statusMeta.name}</span>
                          </PropBtn>
                        }
                      />
                    ) : (
                      <PropValue><StatusIcon status={issue.status} size={13} />{statusMeta.name}</PropValue>
                    )}
                  </PropRow>

                  <PropRow label="Priority">
                    {canWrite ? (
                      <PriorityPicker
                        value={issue.priority}
                        onChange={(v) => updateIssue(issue.id, { priority: v })}
                        trigger={
                          <PropBtn>
                            <PriorityIcon priority={issue.priority} size={13} />
                            <span>{priorityMeta.name}</span>
                          </PropBtn>
                        }
                      />
                    ) : (
                      <PropValue><PriorityIcon priority={issue.priority} size={13} />{priorityMeta.name}</PropValue>
                    )}
                  </PropRow>

                  <PropRow label="Owner">
                    {canWrite ? (
                      <AssigneePicker
                        value={owner?.id ?? null}
                        onChange={(v) => updateIssue(issue.id, { ownerId: v })}
                        kind="human"
                        unassignedLabel="No owner"
                        trigger={
                          <PropBtn>
                            <Avatar user={owner} size={18} />
                            <span>{owner?.name || "No owner"}</span>
                          </PropBtn>
                        }
                      />
                    ) : (
                      <PropValue><Avatar user={owner} size={18} />{owner?.name || "No owner"}</PropValue>
                    )}
                  </PropRow>

                  <PropRow label="Agent">
                    {canWrite ? (
                      <AssigneePicker
                        value={executorAgent?.id ?? null}
                        onChange={(v) =>
                          updateIssue(issue.id, { executorAgentId: v })
                        }
                        kind="agent"
                        unassignedLabel="No agent"
                        trigger={
                          <PropBtn>
                            <Avatar user={executorAgent} size={18} />
                            <span>{executorAgent?.name || "No agent"}</span>
                          </PropBtn>
                        }
                      />
                    ) : (
                      <PropValue>
                        <Avatar user={executorAgent} size={18} />
                        {executorAgent?.name || "No agent"}
                      </PropValue>
                    )}
                  </PropRow>

                  <PropRow label="Reporter">
                    {canWrite ? (
                      <AssigneePicker
                        value={issue.requesterId}
                        onChange={(v) => updateIssue(issue.id, { requesterId: v })}
                        kind="human"
                        unassignedLabel="No reporter"
                        trigger={
                          <PropBtn>
                            <Avatar user={requester} size={18} />
                            <span>{requester?.name || "No reporter"}</span>
                          </PropBtn>
                        }
                      />
                    ) : (
                      <PropValue><Avatar user={requester} size={18} />{requester?.name || "No reporter"}</PropValue>
                    )}
                  </PropRow>

                  <PropRow label="Canal do reporte">
                    <ReportChannelValue
                      channel={issue.reportChannel}
                      context={issue.reportContext}
                      url={issue.reportUrl}
                    />
                  </PropRow>

                  <PropRow label="Labels">
                    {canWrite ? (
                      <LabelPicker
                      teamId={issue.teamId}
                      value={issue.labelIds}
                      onChange={(v) => updateIssue(issue.id, { labelIds: v })}
                      trigger={
                        <PropBtn>
                          {issueLabels.length === 0 ? (
                            <span className="text-[var(--fg-muted)]">No labels</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {issueLabels.slice(0, 3).map((l) => (
                                <span
                                  key={l.id}
                                  className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10.5px] border border-[var(--border)]"
                                >
                                  <span
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{ background: l.color }}
                                  />
                                  {l.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </PropBtn>
                      }
                      />
                    ) : (
                      <PropValue>
                        {issueLabels.length === 0
                          ? "No labels"
                          : issueLabels.slice(0, 3).map((label) => label.name).join(", ")}
                      </PropValue>
                    )}
                  </PropRow>

                  <PropRow label="Project">
                    {canWrite ? (
                      <ProjectPicker
                      value={issue.projectId}
                      teamId={issue.teamId}
                      onChange={(v) => updateIssue(issue.id, { projectId: v })}
                      trigger={
                        <PropBtn>
                          {project ? (
                            <>
                              <span>{project.icon}</span>
                              <span className="truncate">{project.name}</span>
                            </>
                          ) : (
                            <span className="text-[var(--fg-muted)]">No project</span>
                          )}
                        </PropBtn>
                      }
                      />
                    ) : (
                      <PropValue>{project ? <><span>{project.icon}</span>{project.name}</> : "No project"}</PropValue>
                    )}
                  </PropRow>

                  <PropRow label="Due date">
                    {canWrite ? (
                      <div className="flex items-center gap-2">
                        <Calendar size={13} className="text-[var(--fg-tertiary)]" />
                        <input
                          type="date"
                          value={issue.dueDate ? new Date(issue.dueDate).toISOString().slice(0, 10) : ""}
                          onChange={(e) =>
                            updateIssue(issue.id, {
                              dueDate: e.target.value ? new Date(e.target.value).getTime() : null,
                            })
                          }
                          className="bg-transparent outline-none text-[12px] text-[var(--fg-secondary)] border border-[var(--border)] rounded px-1.5 py-0.5"
                        />
                      </div>
                    ) : (
                      <PropValue><Calendar size={13} />{issue.dueDate ? formatDate(issue.dueDate) : "No due date"}</PropValue>
                    )}
                  </PropRow>

                  <TaskAgentExecution
                    issue={issue}
                    agent={executorAgent}
                    canWrite={canWrite}
                  />

                  {issue.dueDate && (
                    <div className="pt-3 border-t border-[var(--border)] text-[11px] text-[var(--fg-muted)]">
                      <div>Due {formatDate(issue.dueDate)}</div>
                    </div>
                  )}
                </div>
              </details>

              {/* Description: rendered markdown by default; click or focus to edit */}
              {editingDescription && canWrite ? (
                <textarea
                  ref={descRef}
                  autoFocus
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onBlur={() => {
                    saveDesc();
                    setEditingDescription(false);
                  }}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                      e.preventDefault();
                      descRef.current?.blur();
                    }
                  }}
                  placeholder="Add a description… (Markdown supported)"
                  className="w-full bg-[var(--bg-secondary)] border border-[var(--border-strong)] rounded-md px-4 py-3 outline-none focus:border-[var(--accent)] text-[13.5px] text-[var(--fg)] resize-y min-h-[220px] leading-relaxed font-mono"
                />
              ) : description.trim() ? (
                <div
                  onClick={(e) => {
                    // Don't hijack clicks on internal anchors/buttons (wikilinks, mentions, tags).
                    // Only switch to edit mode when the click really is on empty markdown surface.
                    const t = e.target as HTMLElement;
                    if (t.closest("a, button, input, textarea, summary, select")) return;
                    if (canWrite) setEditingDescription(true);
                  }}
                  className={canWrite
                    ? "prose-doc min-w-0 cursor-text rounded-md hover:bg-[var(--bg-secondary)]/50 transition-colors"
                    : "prose-doc min-w-0 rounded-md"
                  }
                  title={canWrite ? "Click to edit" : undefined}
                >
                  <Markdown
                    content={description}
                    readingTitle={issue.title}
                    onImageAnnotated={canWrite
                      ? (oldUrl, newUrl) => {
                          const next = replaceCurrentHumanMarkdown(description, oldUrl, newUrl);
                          setDescription(next);
                          updateIssue(issue.id, { description: next });
                        }
                      : undefined}
                  />
                </div>
              ) : canWrite ? (
                <button
                  onClick={() => setEditingDescription(true)}
                  className="text-left w-full text-[14px] text-[var(--fg-muted)] italic rounded-md py-3 min-h-[120px] hover:bg-[var(--bg-secondary)]/50"
                >
                  Add a description… (Markdown supported)
                </button>
              ) : (
                <div className="rounded-md py-3 min-h-[80px] text-[14px] italic text-[var(--fg-muted)]">
                  No description.
                </div>
              )}

              <div className="pt-6">
                <AgentRunsSection
                  scope={{ kind: "issue", id: issue.id }}
                  emptyHidden
                  limit={20}
                />
              </div>

              <div className="pt-6 border-t border-[var(--border)]">
                <div className="flex items-center gap-2 mb-3 text-[12px] font-medium text-[var(--fg-secondary)]">
                  <MessageSquare size={13} />
                  Activity
                  <span className="text-[var(--fg-muted)]">{activity.length + issue.comments.length}</span>
                </div>

                <div className="space-y-1 mb-4">
                  {(() => {
                    type Item =
                      | { kind: "event"; ts: number; ev: ActivityEvent }
                      | { kind: "comment"; ts: number; c: typeof issue.comments[number] };
                    const items: Item[] = [
                      ...activity.map((ev) => ({
                        kind: "event" as const,
                        ts: new Date(ev.created_at).getTime(),
                        ev,
                      })),
                      ...issue.comments.map((c) => ({
                        kind: "comment" as const,
                        ts: new Date(c.createdAt).getTime(),
                        c,
                      })),
                    ].sort((a, b) => a.ts - b.ts);

                    return items.map((it) => {
                      if (it.kind === "event") {
                        const u = users.find((x) => x.id === it.ev.actor_id);
                        const isBot = !!(u?.email?.startsWith("bot+") || u?.email?.endsWith("@bots.pauta.dev"));
                        return (
                          <ActivityEventRow
                            key={`e-${it.ev.id}`}
                            ev={it.ev}
                            actorName={u?.name ?? "Unknown"}
                            actorColor={u?.color ?? "var(--fg-muted)"}
                            actorInitials={u?.initials ?? "?"}
                            isBot={isBot}
                          />
                        );
                      }
                      const c = it.c;
                      const u = users.find((x) => x.id === c.userId);
                      const commenterIsBot = !!(
                        u?.email?.startsWith("bot+") || u?.email?.endsWith("@bots.pauta.dev")
                      );
                      return (
                        <div key={`c-${c.id}`} className="flex gap-2.5 py-1.5">
                          <Avatar user={u} size={24} />
                          <div className="flex-1 min-w-0 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[12.5px] font-medium">{u?.name}</span>
                              {commenterIsBot && (
                                <span
                                  className="inline-flex items-center gap-0.5 px-1 rounded text-[9px] font-semibold uppercase tracking-wide bg-[var(--accent-bg)] text-[var(--accent)]"
                                  title="Comment posted by an agent"
                                >
                                  🤖 agent
                                </span>
                              )}
                              <span className="text-[11px] text-[var(--fg-muted)]">
                                {formatRelative(c.createdAt)}
                              </span>
                            </div>
                            {c.replyToId && <p className="mb-1 text-[11px] text-[var(--fg-muted)]">Resposta a {users.find((person) => person.id === issue.comments.find((parent) => parent.id === c.replyToId)?.userId)?.name ?? "comentário anterior"}</p>}
                            <div className="text-[12.5px] text-[var(--fg-secondary)] prose-doc">
                              <Markdown content={c.body} />
                            </div>
                            {canWrite && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(c.id) && <button type="button" disabled={commentSaving} className="focus-ring min-h-11 px-2 text-xs text-[var(--accent)]" onClick={() => { setReplyToId(c.id); setCommentError(null); commentRef.current?.focus(); }}>Responder a {u?.name ?? "comentário"}</button>}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>

                {canWrite && (
                <div className="flex gap-2">
                  <Avatar user={me} size={24} />
                  <div className="flex-1 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] focus-within:border-[var(--accent)]">
                    {replyToId && <div className="flex flex-wrap items-center gap-2 px-3 text-xs"><span>Respondendo a {users.find((person) => person.id === issue.comments.find((parent) => parent.id === replyToId)?.userId)?.name ?? "comentário"}</span><button type="button" disabled={commentSaving} className="focus-ring min-h-11 px-2 text-[var(--accent)]" onClick={() => setReplyToId(null)}>Cancelar resposta</button></div>}
                    {commentError && <p role="alert" className="px-3 py-2 text-xs text-[var(--red)]">{commentError}</p>}
                    <textarea
                      ref={commentRef}
                      disabled={commentSaving}
                      aria-label={replyToId ? "Sua resposta" : "Seu comentário"}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Leave a comment…"
                      className="w-full bg-transparent outline-none text-[12.5px] p-3 resize-none min-h-[60px]"
                      onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && comment.trim()) {
                          e.preventDefault(); void submitComment();
                        }
                      }}
                    />
                    <div className="flex flex-wrap items-center justify-end gap-2 px-2 pb-2">
                      <HumanMentionPicker workspaceId={issue.workspaceId} onMention={(token) => setComment((current) => `${current}${current && !current.endsWith(" ") ? " " : ""}${token} `)} />
                      <span className="text-[10.5px] text-[var(--fg-muted)]">
                        <kbd className="kbd">⌘</kbd> <kbd className="kbd">Enter</kbd> to send
                      </span>
                      <button
                        disabled={!comment.trim() || commentSaving}
                        onClick={() => void submitComment()}
                        className="focus-ring min-h-11 px-2.5 py-1 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-40 disabled:hover:bg-[var(--accent)] text-white text-[11.5px] font-medium transition-colors"
                      >
                        {commentSaving ? "Enviando…" : replyToId ? "Enviar resposta" : "Comentar"}
                      </button>
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      </>
      )}
    </AnimatePresence>
  );
}

function PropRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[80px_1fr] items-start gap-2">
      <div className="text-[11.5px] text-[var(--fg-tertiary)] pt-1">{label}</div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function PropBtn({ children }: { children: React.ReactNode }) {
  return (
    <button className="inline-flex items-center gap-1.5 px-1.5 py-1 rounded-md hover:bg-[var(--bg-hover)] text-[12px] text-[var(--fg-secondary)] max-w-full text-left [overflow-wrap:anywhere]">
      {children}
    </button>
  );
}

function PropValue({ children }: { children: React.ReactNode }) {
  return (
    <div className="inline-flex max-w-full items-center gap-1.5 px-1.5 py-1 text-[12px] [overflow-wrap:anywhere] text-[var(--fg-secondary)]">
      {children}
    </div>
  );
}

type ActivityEvent = {
  id: string;
  actor_id: string | null;
  action: "create" | "update" | "status_change" | "assign" | "delete";
  diff: { before?: Record<string, unknown>; after?: Record<string, unknown> } | null;
  created_at: string;
};

function ActivityEventRow({
  ev,
  actorName,
  actorColor,
  actorInitials,
  isBot,
}: {
  ev: ActivityEvent;
  actorName: string;
  actorColor: string;
  actorInitials: string;
  isBot: boolean;
}) {
  const meta: Record<ActivityEvent["action"], { icon: typeof Plus; color: string; verb: string }> = {
    create: { icon: Plus, color: "var(--green)", verb: "created this task" },
    update: { icon: Edit, color: "var(--fg-tertiary)", verb: "updated" },
    status_change: { icon: ArrowRight, color: "var(--blue)", verb: "moved status" },
    assign: { icon: UserPlus, color: "var(--purple)", verb: "changed assignee" },
    delete: { icon: Trash2, color: "var(--red)", verb: "deleted" },
  };
  const m = meta[ev.action];
  const Icon = m.icon;

  let detail: string | null = null;
  if (ev.action === "status_change") {
    const before = ev.diff?.before?.status as string | undefined;
    const after = ev.diff?.after?.status as string | undefined;
    if (before && after) detail = `${before} → ${after}`;
  } else if (ev.action === "update" && ev.diff?.after) {
    const keys = Object.keys(ev.diff.after).filter((k) => k !== "updatedAt");
    if (keys.length) detail = keys.join(", ");
  }

  return (
    <div className="flex items-start gap-2.5 py-1.5">
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-semibold text-white shrink-0"
        style={{ background: actorColor }}
        title={actorName}
      >
        {isBot ? "🤖" : actorInitials}
      </div>
      <div className="flex-1 min-w-0 text-[12px] text-[var(--fg-secondary)] leading-[20px]">
        <span className="font-medium text-[var(--fg)]">{actorName}</span>
        {isBot && (
          <span
            className="inline-flex items-center gap-0.5 ml-1.5 px-1 rounded text-[9px] font-semibold uppercase tracking-wide bg-[var(--accent-bg)] text-[var(--accent)] align-middle"
            title="Action performed by an agent (MCP token)"
          >
            🤖 agent
          </span>
        )}{" "}
        <Icon size={11} className="inline -mt-0.5" style={{ color: m.color }} /> {m.verb}
        {detail ? <span className="text-[var(--fg-muted)]"> · {detail}</span> : null}
        <span className="text-[var(--fg-muted)]"> · {new Date(ev.created_at).toLocaleString()}</span>
      </div>
    </div>
  );
}
